import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { tsApi, type SessionDetail } from "../../../api/timesheets";
import { Drawer } from "../../../components/Overlay";
import { LoadState, useLoad } from "../shared";
import { BreaksEditor } from "./BreaksEditor";

const H = ({ children }: { children: string }) => <h3 className="label-caps mb-2 mt-6 text-signal">{children}</h3>;

export function SessionDrawer({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [n, setN] = useState(0);
  const loader = useCallback(() => tsApi.detail(id), [id, n]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data: d, error } = useLoad<SessionDetail>(loader);
  const changed = () => { setN((x) => x + 1); onChanged(); };
  return (
    <Drawer open title="Session details" onClose={onClose} testId="session-drawer">
      {!d ? <LoadState error={error} loading={!error} testId="session-drawer-load" /> : (
        <div className="text-xs text-zinc-300">
          <p className="text-zinc-500">{d.venue_name} · times in {d.timezone}</p>
          <H>Evidence timeline (read-only)</H>
          <ol data-testid="session-timeline" className="space-y-1">
            {d.timeline.map((e, i) => <li key={i} className="rounded-sm bg-ink p-2">{e.type} · {e.source} · SMS {e.sms ?? "—"} · GPS {e.gps ?? "—"}{e.delay_sec != null && ` · delay ${Math.round(e.delay_sec / 60)} min`}</li>)}
            {!d.timeline.length && <li className="text-zinc-500">No punch events.</li>}
          </ol>
          <H>Revision history</H>
          <ol data-testid="session-revisions" className="space-y-1">
            {d.revisions.map((r) => (
              <li key={r.revision_no} data-testid={`revision-${r.revision_no}`} className="rounded-sm border border-white/10 p-2">
                <span className="font-mono text-white">#{r.revision_no}</span> {r.source} · {r.in_local ?? "—"} → {r.out_local ?? "—"}
                <span className="block text-zinc-500">{r.actor_name} · {new Date(r.created_at).toLocaleString()}{r.reason ? ` · “${r.reason}”` : ""}</span>
              </li>
            ))}
          </ol>
          <H>Breaks</H>
          <BreaksEditor sessionId={id} breaks={d.breaks} onChanged={changed} />
          <H>Corrections & exceptions</H>
          <ul data-testid="session-links" className="space-y-1">
            {d.corrections.map((c) => <li key={c.id}><Link className="text-signal" to={`/admin/corrections?open=corrections:${c.id}`}>Correction {c.requested_values.category ?? c.requested_values.kind} · {c.status}</Link></li>)}
            {d.exceptions.map((e) => <li key={e.id}><Link className="text-signal" to={`/admin/corrections?open=exceptions:${e.id}`}>Exception {e.code} · {e.status}</Link></li>)}
            {!d.corrections.length && !d.exceptions.length && <li className="text-zinc-500">None.</li>}
          </ul>
        </div>
      )}
    </Drawer>
  );
}
